// The languages the site speaks. English is the default and keeps the
// unprefixed URLs; Spanish, Portuguese, Egyptian Arabic, French and German pages live under
// /es, /pt, /ar, /fr and /de.
export const LOCALES = ['en', 'es', 'pt', 'ar', 'fr', 'de'];
export const DEFAULT_LOCALE = 'en';

// What each locale is called in its own language, for switchers
export const LOCALE_NAMES = { en: 'English', es: 'Español', pt: 'Português', ar: 'العربية', fr: 'Français', de: 'Deutsch' };

// <html lang>, og:locale and Clerk's translation for each
export const LOCALE_TAGS = { en: 'en', es: 'es', pt: 'pt-BR', ar: 'ar-EG', fr: 'fr', de: 'de' };
export const OG_LOCALES = { en: 'en_US', es: 'es_LA', pt: 'pt_BR', ar: 'ar_EG', fr: 'fr_FR', de: 'de_DE' };

// Arabic reads right to left: <html dir> follows the page (App.tsx), so every
// dialog and menu mounted on <body> flips with it
const RTL = new Set(['ar']);
export const dirFor = (locale) => (RTL.has(locale) ? 'rtl' : 'ltr');

// The tag for Intl dates and numbers. Arabic keeps Western digits (0-9), as
// Egyptian sites write prices and dates, rather than Intl's default ٠١٢
export const INTL_TAGS = { en: 'en-US', es: 'es-419', pt: 'pt-BR', ar: 'ar-EG-u-nu-latn', fr: 'fr-FR', de: 'de-DE' };
export const intlTag = (locale) => INTL_TAGS[locale] || INTL_TAGS.en;

const STORAGE_KEY = 'cs_locale';

/** The locale a URL is written in: 'es' for /es and /es/…, 'pt', 'ar', 'fr' and 'de' likewise, else null. */
export function localeFromPath(pathname = '') {
  const match = /^\/(es|pt|ar|fr|de)(?:\/|$)/.exec(pathname);
  return match ? match[1] : null;
}

/** The first of the browser's languages that the site speaks, or null. */
export function browserLocale(languages) {
  const list = languages
    ?? (typeof navigator !== 'undefined' ? (navigator.languages?.length ? navigator.languages : [navigator.language]) : []);
  for (const tag of list || []) {
    const base = String(tag || '').toLowerCase().split('-')[0];
    if (LOCALES.includes(base)) return base;
  }
  return null;
}

/** The locale the visitor chose or last read the site in, if any. */
export function storedLocale() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return LOCALES.includes(value) ? value : null;
  } catch {
    return null;
  }
}

export function rememberLocale(locale) {
  if (!LOCALES.includes(locale)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Private windows and blocked storage: the choice lasts for this page only
  }
}

/** The visitor's language when the URL does not say: their choice, else their browser's, else English. */
export function preferredLocale() {
  return storedLocale() || browserLocale() || DEFAULT_LOCALE;
}

// Pages that belong to the signed-in app (and its doors). They keep one URL for
// every language and speak the visitor's preferred one; every other unprefixed
// page is an English marketing page and stays English, whatever the browser says,
// so a search engine sees the same page a person does.
const APP_PATH = /^\/(ai-agent|ai-clipping|auto-caption|video-reframe|editor|account|admin|login|signup|subscription|v)(?:\/|$)/;

export function isAppPath(pathname = '') {
  return APP_PATH.test(pathname);
}

/** The language a page at `pathname` is shown in. */
export function pageLocale(pathname = '', preferred = preferredLocale()) {
  return localeFromPath(pathname) || (isAppPath(pathname) ? preferred : DEFAULT_LOCALE);
}
